import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "selectAllBtn", "applyBtn", "dropdownToggle", "countDisplay", "searchInput", "item", "emptyState"]
  static values = { 
    selectAllText: String,
    deselectAllText: String 
  }

  connect() {
    this.initializeFromFormState()
    this.updateSelectAllButton()
    this.updateApplyButton()
    this.updateCountDisplay()
    this.filterVisibleItems()
  }

  initializeFromFormState() {
    // Sync dropdown checkboxes with form state on page load
    this.checkboxTargets.forEach(checkbox => {
      const marketId = checkbox.value
      const formCheckbox = document.querySelector(`[data-market-checkbox="${marketId}"]`)
      if (formCheckbox) {
        checkbox.checked = formCheckbox.checked
      }
    })
  }

  toggleMarket(event) {
    this.updateSelectAllButton()
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  filterItems() {
    this.filterVisibleItems()
  }

  toggleMarketItem(event) {
    // Don't toggle if user clicked directly on the checkbox (to avoid double toggle)
    if (event.target.type === 'checkbox') {
      return
    }
    
    // Find the checkbox within this dropdown item
    const checkbox = event.currentTarget.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.checked = !checkbox.checked
      this.updateSelectAllButton()
      this.updateApplyButton()
      this.updateCountDisplay()
    }
  }

  toggleSelectAll() {
    const allChecked = this.checkboxTargets.every(checkbox => checkbox.checked)
    
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = !allChecked
    })
    
    this.updateSelectAllButton()
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  applyFilters() {
    // 1. Sync dropdown checkboxes with form checkboxes
    this.syncWithFormCheckboxes()
    
    // 2. Sync with other markets dropdown instances
    this.syncOtherMarketsDropdowns()
    
    // 3. Submit the shared form (which will update municipalities and auto-check them)
    this.submitSharedForm()
    
    // 4. Close the dropdown
    this.closeDropdown()
  }

  syncWithFormCheckboxes() {
    this.checkboxTargets.forEach(checkbox => {
      const marketId = checkbox.value
      const formCheckbox = document.querySelector(`[data-market-checkbox="${marketId}"]`)
      if (formCheckbox) {
        formCheckbox.checked = checkbox.checked
      }
    })
  }

  syncOtherMarketsDropdowns() {
    // Find all other markets dropdown controllers and sync their checkboxes
    const allMarketsDropdowns = document.querySelectorAll('[data-controller*="active-markets-dropdown"]')
    allMarketsDropdowns.forEach(dropdown => {
      if (dropdown !== this.element) {
        // Get the controller instance for this dropdown
        const otherController = this.application.getControllerForElementAndIdentifier(dropdown, 'active-markets-dropdown')
        if (otherController) {
          // Sync checkboxes to match current state
          otherController.checkboxTargets.forEach(otherCheckbox => {
            const marketId = otherCheckbox.value
            const currentCheckbox = this.checkboxTargets.find(cb => cb.value === marketId)
            if (currentCheckbox) {
              otherCheckbox.checked = currentCheckbox.checked
            }
          })
          // Update UI elements
          otherController.updateCountDisplay()
          otherController.updateApplyButton()
        }
      }
    })
  }

  submitSharedForm() {
    const form = document.getElementById('auction-filters-form')
    if (form) {
      // Only let the first/desktop instance handle municipality updates to avoid conflicts
      if (this.shouldHandleMunicipalityUpdates()) {
        // Clear municipality form state before processing new markets
        this.clearAllMunicipalityFormState()
        this.updateMunicipalitiesDropdown()
      }
      form.requestSubmit()
    }
  }

  shouldHandleMunicipalityUpdates() {
    // Let any markets dropdown handle municipality updates, but prevent duplicates
    // Check if another instance is already updating (to prevent race conditions)
    if (window.municipalityUpdateInProgress) {
      return false
    }
    return true
  }

  clearAllMunicipalityFormState() {
    // Clear all municipality form checkboxes to start fresh
    const municipalityCheckboxes = document.querySelectorAll('[data-municipality-checkbox]')
    municipalityCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })

    const neighborhoodCheckboxes = document.querySelectorAll('[data-neighborhood-checkbox]')
    neighborhoodCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })
  }

  updateMunicipalitiesDropdown() {
    // Set flag to prevent duplicate updates
    window.municipalityUpdateInProgress = true
    
    // Create a form that targets the municipalities with turbo stream
    const selectedMarkets = this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
    
    // Build URL with query parameters
    const url = new URL('/auctions/municipalities', window.location.origin)
    selectedMarkets.forEach(marketId => {
      url.searchParams.append('selected_markets[]', marketId)
    })
    
    // Use turbo stream to update both desktop and mobile dropdowns
    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(response => response.text())
    .then(html => {
      // Turbo will automatically process the stream response
      // The stream should update both desktop and mobile containers
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error updating municipalities dropdown:', error)
    })
    .finally(() => {
      // Clear flag when done
      window.municipalityUpdateInProgress = false
    })
  }


  closeDropdown() {
    const toggleButton = this.dropdownToggleTarget
    if (toggleButton) {
      // Use Bootstrap's dropdown API to hide
      const dropdown = bootstrap.Dropdown.getInstance(toggleButton)
      if (dropdown) {
        dropdown.hide()
      }
    }
  }

  updateSelectAllButton() {
    if (!this.hasSelectAllBtnTarget) return
    
    const allChecked = this.checkboxTargets.every(checkbox => checkbox.checked)
    const someChecked = this.checkboxTargets.some(checkbox => checkbox.checked)
    
    if (allChecked) {
      this.selectAllBtnTarget.textContent = this.deselectAllTextValue || "Αποεπιλογή όλων"
    } else {
      this.selectAllBtnTarget.textContent = this.selectAllTextValue || "Επιλογή όλων"
    }
  }

  updateApplyButton() {
    if (!this.hasApplyBtnTarget) return
    
    const selectedCount = this.checkboxTargets.filter(checkbox => checkbox.checked).length
    this.applyBtnTarget.disabled = selectedCount === 0
  }

  updateCountDisplay() {
    if (!this.hasCountDisplayTarget) return
    
    const selectedCount = this.checkboxTargets.filter(checkbox => checkbox.checked).length
    const currentText = this.countDisplayTarget.textContent
    const baseText = currentText.replace(/\(\d+\)/, '') // Remove existing count
    this.countDisplayTarget.textContent = `${baseText.trim()} (${selectedCount})`
  }

  filterVisibleItems() {
    if (!this.hasSearchInputTarget || !this.hasItemTarget) return

    const query = this.normalizedText(this.searchInputTarget.value)
    let visibleCount = 0

    this.itemTargets.forEach(item => {
      const matches = this.normalizedText(item.dataset.filterLabel).includes(query)
      item.classList.toggle('d-none', !matches)
      if (matches) visibleCount += 1
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('d-none', visibleCount > 0)
    }
  }

  normalizedText(value) {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
  }
}
